/*!

=========================================================
* Vision UI Free Chakra - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-chakra
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-chakra/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useEffect, useMemo } from "react";

// Chakra imports
import { Flex, Table, Tbody, Text, Th, Thead, Tr, Input, Select } from "@chakra-ui/react";

// Custom components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";

// Table Components

// Data

// Icons
import { useLazyQuery } from "@apollo/client";
import SkeletonTable from "components/Tables/SkeletonTable";
import TablesValidatorsRow from "components/Tables/TablesValidatorsRow";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { FETCH_OPERATORS } from "utils/queries";

function Validators() {
  const [validatorsData, setValidatorsData] = useState([]);
  const { validatorAddress } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    operatorId: "",
    address: "",
    status: "",
    stake: "",
  });
  const [getValidators, { data }] = useLazyQuery(FETCH_OPERATORS, {
    variables: {
      orderBy: `operatorId`,
      orderDirection: `asc`,
      first: 1000,
      skip: 0,
    },
  });

  useEffect(() => {
    fetchAllValidators();
    console.log(validatorAddress);
    return () => {};
  }, []);

  useEffect(() => {
    data?.operators && setValidatorsData(data.operators);
  }, [data]);

  const fetchAllValidators = async () => {
    setIsLoading(true);
    await getValidators();
    setIsLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filteredValidators = useMemo(() => {
    return validatorsData.filter((validator) => {
      if (
        filters.operatorId &&
        !validator.operatorId?.toString().includes(filters.operatorId)
      ) {
        return false;
      }

      if (
        filters.address &&
        !validator.address?.toLowerCase().includes(filters.address.toLowerCase())
      ) {
        return false;
      }

      if (filters.status && filters.status !== "All") {
        if (filters.status !== validator.status) return false;
      }

      if (filters.stake) {
        const stakeValue = validator.stake / 1e18;
        const filterStake = parseFloat(filters.stake);
        if (isNaN(filterStake) || stakeValue < filterStake) {
          return false;
        }
      }

      return true;
    });
  }, [validatorsData, filters]);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* Authors Table */}
      <Card
        overflowX={{ sm: "scroll", xl: "hidden" }}
        pb="0px"
        className="scroll-content"
      >
        <CardHeader p="6px 0px 22px 0px">
          <Text fontSize="lg" color="#fff" fontWeight="bold">
            Operators Table
          </Text>
        </CardHeader>
        <CardBody>
          <Table variant="simple" color="#fff">
            <Thead>
              <Tr my=".8rem" ps="0px" color="gray.400">
                <Th
                  ps="0px"
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  ID
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Operator
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Status
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Stake
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  APY
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Active
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Finalized Block
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Attested Dynasty
                </Th>
                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Attested Epoch
                </Th>

                <Th
                  color="gray.400"
                  fontFamily="Plus Jakarta Display"
                  borderBottomColor="#56577A"
                >
                  Last Epoch Churned Out
                </Th>
              </Tr>
              {/* Filter Row */}
              <Tr>
                <Th ps="0px" borderBottomColor="#56577A">
                  <Input
                    placeholder="1"
                    size="sm"
                    value={filters.operatorId}
                    onChange={(e) => handleFilterChange("operatorId", e.target.value)}
                    bg="transparent"
                    color="white"
                    borderColor="#56577A"
                    style={{ borderRadius: "8px" }}
                    _hover={{ borderColor: "#4FD1C5" }}
                    _focus={{ borderColor: "#4FD1C5", boxShadow: "none" }}
                  />
                </Th>
                <Th borderBottomColor="#56577A">
                  <Input
                    placeholder="Filter Address"
                    size="sm"
                    value={filters.address}
                    onChange={(e) => handleFilterChange("address", e.target.value)}
                    bg="transparent"
                    color="white"
                    borderColor="#56577A"
                    style={{ borderRadius: "8px" }}
                    _hover={{ borderColor: "#4FD1C5" }}
                    _focus={{ borderColor: "#4FD1C5", boxShadow: "none" }}
                  />
                </Th>
                <Th borderBottomColor="#56577A">
                  <Select
                    size="sm"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    defaultValue="All"
                    bg="transparent"
                    color="white"
                    borderColor="#56577A"
                    style={{ borderRadius: "8px" }}
                    _hover={{ borderColor: "#4FD1C5" }}
                    _focus={{ borderColor: "#4FD1C5", boxShadow: "none" }}
                  >
                    <option value="All" style={{ background: "#0f1535" }}>
                      All
                    </option>
                    <option value="Active" style={{ background: "#0f1535" }}>
                      Active
                    </option>
                    <option value="Inactive" style={{ background: "#0f1535" }}>
                      Inactive
                    </option>
                    <option value="Not Selected" style={{ background: "#0f1535" }}>
                      Not Selected
                    </option>
                  </Select>
                </Th>
                <Th borderBottomColor="#56577A">
                  <Input
                    placeholder="Min Stake"
                    size="sm"
                    type="number"
                    value={filters.stake}
                    onChange={(e) => handleFilterChange("stake", e.target.value)}
                    bg="transparent"
                    color="white"
                    borderColor="#56577A"
                    style={{ borderRadius: "8px" }}
                    _hover={{ borderColor: "#4FD1C5" }}
                    _focus={{ borderColor: "#4FD1C5", boxShadow: "none" }}
                  />
                </Th>
                <Th borderBottomColor="#56577A"></Th>
                <Th borderBottomColor="#56577A"></Th>
                <Th borderBottomColor="#56577A"></Th>
                <Th borderBottomColor="#56577A"></Th>
                <Th borderBottomColor="#56577A"></Th>
                <Th borderBottomColor="#56577A"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <SkeletonTable rowCount={10} columnCount={10} />
              ) : filteredValidators.length === 0 ? (
                <TablesValidatorsRow
                  address=""
                  stake={null}
                  operatorId={null}
                  status={true}
                  lastItem={true}
                  apy={null}
                />
              ) : (
                filteredValidators.map((row, index, arr) => {
                  return (
                    <TablesValidatorsRow
                      key={index}
                      address={row.address}
                      operatorId={row.operatorId}
                      stake={row.stake / 1e18}
                      apy={row.apy}
                      lastAttestedDynasty={row.lastAttestedDynasty}
                      lastAttestedEpoch={row.lastAttestedEpoch}
                      lastFinalizedBlock={row.lastFinalizedBlock}
                      status={row.status}
                      lastActiveTimestamp={row.lastActiveTimestamp}
                      lastEpochChurnedOut={row.lastEpochChurnedOut}
                      lastItem={index === arr.length - 1 ? true : false}
                    />
                  );
                })
              )}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default Validators;
